import { BarChartRounded, Info, ListRounded } from '@mui/icons-material'
import { Box, IconButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'
import useBestCTR from '../hooks/useBestCTR'
import { HeroContent, InfoContent, PeriodButton } from '../styles/GraphsStyles'
import CustomVerticalBarChart from './CustomVerticalBarChart'

enum Period {
  week,
  month
}

function BestCTR() {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)

  const [display, setDisplay] = useState<number>(1)
  const [period, setPeriod] = useState<Period>(Period.week)
  const [timeGap, setTimeGap] = useState<string | null>('week')
  const { weekCTR, monthCTR } = useBestCTR()

  const handleTimeGap = (_: React.MouseEvent<HTMLElement>, newTimeGap: string | null) => {
    setTimeGap(newTimeGap)
  }

  const changeDisplayMode = (mode: number) => {
    setDisplay(mode)
  }

  const getRowId = (row: any) => row.foreign_id
  const rows: GridRowsProp<any> = period === Period.week ? weekCTR : (monthCTR ?? [])
  const columns: GridColDef[] = [
    { field: 'position', headerName: '#' },
    {
      field: 'foreign_name',
      headerName: t('name'),
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip placement="top" TransitionProps={{ timeout: 400 }} title={params.row.name}>
          <Box display="flex" alignItems="center" gap={1} my={1} width="100%">
            <Box
              style={{
                backgroundImage: `url(${params.row.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                height: '32px',
                width: '32px',
                borderRadius: '4px'
              }}
            />
            <InfoContent display="flex" flexDirection="column">
              <Typography variant="body2">{params.value}</Typography>
              <Typography variant="caption">{t(params.row.menu)}</Typography>
            </InfoContent>
          </Box>
        </Tooltip>
      )
    },
    { field: 'value', headerName: t('CTR') }
  ]

  return (
    <Box width="100%">
      <Box width='100%' display='flex' justifyContent="space-between" alignItems='center' flexDirection={isMobile ? 'column' : 'row'}>
        <HeroContent>
          <Typography variant="subtitle1">{t('videos-with-best-ctr')}</Typography>
          <Tooltip
            placement="bottom"
            title={
              <Box maxWidth={'240px'}>
                <Typography variant="caption">{t('videos-ctr-quote')}</Typography>
              </Box>
            }
          >
            <Info fontSize="small" />
          </Tooltip>
        </HeroContent>
        <Box display='flex' justifyContent='flex-end' flexDirection='row' alignItems='center'>
          <ToggleButtonGroup
            value={timeGap}
            exclusive
            aria-label="time gap"
            size="small"
            onChange={handleTimeGap}
            sx={{ maxHeight: 32, padding: 2 }}
          >
            <PeriodButton onClick={() => setPeriod(Period.month)} value="month" aria-label="month">
              <Typography variant="body2">{t('monthly')}</Typography>
            </PeriodButton>
            <PeriodButton onClick={() => setPeriod(Period.week)} value="week" aria-label="week">
              <Typography variant="body2">{t('weekly')}</Typography>
            </PeriodButton>
          </ToggleButtonGroup>
          {/* Botón para cambiar de visualización(display) entre gráficas o tabla */}
          {display ? (
            <Tooltip title={`${t('change-view-to-list')}`}>
              <IconButton onClick={() => changeDisplayMode(0)}>
                <ListRounded color="secondary" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={`${t('change-view-to-graphic')}`}>
              <IconButton onClick={() => changeDisplayMode(1)}>
                <BarChartRounded color="secondary" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      {display ? (
        <CustomVerticalBarChart data={period === Period.week ? weekCTR : monthCTR} dataKey={'value'} typeDataName={'CTR'} />
      ) : (
        <Box mt={isMobile ? 2 : 0}>
          <div className="table_analitics">
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={getRowId}
              disableColumnMenu
              autoHeight
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 6, page: 0 }
                }
              }}
              pageSizeOptions={[6]}
            />
          </div>
        </Box>
      )}
    </Box>
  )
}

export default BestCTR
